
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Flex, Box, Text, Image } from 'rebass/styled-components'
import { toast } from 'react-toastify';
import parse, { Element as ParserElement } from 'html-react-parser';
import { GET_CLINICAL_TEAM_CONVERSATION_ID, GET_CONVERSATIONS, GET_CONVERSATION_BY_ID } from '../../../graphql/queries';
import { ADD_NOTE_TO_CONVERSATION, CHANGE_CONVERSATION_STATUS, REASSIGN_CONVERSATION, REPLY_TO_CONVERSATION } from '../../../graphql/mutations';
import { IGetClinicalTeamConversationIdInput, IGetClinicalTeamConversationIdOutput, IGetConversationByIdOutput, IIGetConversationByIdInput, IThread, IThreadOutput } from '../../../graphql/types/queries/conversation';
import { IUser } from '../../../graphql/types/queries/user';
import withSession from '../../../lib/withSession';
import styled from 'styled-components';
import { MessageAvatarNoPicStyled, MessageAvatarStyled, MessageTextStyled, SmsStyleBubbleMe, SmsStyleBubbleNoteMe, SmsStyleBubbleNoteOther, SmsStyleBubbleOther, SmsStyleBubbleWorkflow } from '../../../components/conversation/styled/bubbles';
import Modal from 'styled-react-modal';
import moment from 'moment';
import { replaceAll } from '../../../utils/StringUtils';
import { IAddNoteToConversationInput, IAddNoteToConversationOutput, IProviderChangeStatusConversationInput, IProviderChangeStatusConversationOutput, IProviderReassignConversationInput, IProviderReassignConversationOutput, IReplyToConversationInput, IReplyToConversationOutput } from '../../../graphql/types/mutations/conversation';
import { ConversationStatusCard } from './ConversationStatusCard';
import PleaseWait from '../../../components/PleaseWait';
import { LoadingMessages } from '../../../components/icons/LoadingMessages';
import { useNavigate } from 'react-router-dom';
import Select, { ActionMeta } from 'react-select';
import { ReactSelectStyles } from '../../../styles/ReactSelectStyles';
import { MessengerEmbeddedInner } from './innerComponents/MessengerEmbeddedInner';
import { NoClinicalTeamConversation } from './innerComponents/NoClinicalTeamConversation';

interface ICommunicatorMessagerProps {
  conversationId?: string,
  session: IUser,
  onSelectConversation?: (c: string) => void,
  athenaId: string
  careTeamConvoId?: string
  isHpiEmbed?:boolean
}

const getDateText = (thread: IThread) => {
  const date = moment(thread.createdAt)
  const diffHours = moment().diff(date, 'hours')
  if (diffHours < 1) {
    const diffMinutes = moment().diff(date, 'minutes')
    return `${diffMinutes} min${diffMinutes > 1 ? 's' : ''} ago`
  }

  if (diffHours < 24) {
    return `${diffHours} hr${diffHours > 1 ? 's' : ''} ago`
  }

  if (moment().isSame(date, 'year')) {
    return date.format('MMM DD, h:mma')
  } else {
    return date.format('MMM DD YYYY, h:mma')
  }
}

const getDayText = (thread: IThread) => {
  const date = moment(thread.createdAt)
  // const diffHours = moment().diff(date, 'hours')
  if (date.isSame(moment(), 'day')) {
    return `Today`
  }
  if (date.isAfter(moment().add(-7, 'day'))) {
    return date.format('dddd')
  }

  if (moment().isSame(date, 'year')) {
    return date.format('MMM DD')
  } else {
    return date.format('MMM DD YYYY')
  }
}
const LineItem = ({ thread, previousThread, isPatientConvo }: { isPatientConvo: boolean, thread: IThread, previousThread?: IThread | null }) => {
  const isSameDay = !previousThread ? false : moment(thread.createdAt).isSame(moment(previousThread.createdAt), 'day')
  return (
    <Flex width={'100%'} flexDirection={'column'} alignItems={'center'} justifyContent={'center'}
      mb={'16px'}>
      {!isSameDay && <Box style={{ background: !isPatientConvo ? '#E7BFB9' : '#7F91D4', padding: '4px 8px', minWidth: '150px', borderRadius: '12px' }} mb={3} alignSelf={'center'}><Text fontSize={'14px'} fontWeight={600} textAlign={'center'}>{getDayText(thread)}</Text></Box>}
      <SmsStyleBubbleWorkflow>
        {(thread.action?.type || '').toLowerCase().indexOf('workflow') > -1 && (
          <svg width="12" height="22" viewBox="0 0 12 22" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 14H0L7 0V8H12L5 22V14Z" fill="white" />
          </svg>
        )}

        <Flex flex={1} ml={3} justifyContent={'center'} alignItems={'center'}>
          <MessageTextStyled fontSize={'14px'} textAlign={'left'} alignSelf={'center'}
            dangerouslySetInnerHTML={{ __html: replaceAll((thread.action?.text || ''), 'You ', 'G from Paloma Health ').replace(' **', '&nbsp;<strong>').replace('** ', '</strong>&nbsp;') }}
          />
        </Flex>
      </SmsStyleBubbleWorkflow>
      <Text mt={1} fontSize={'12px'} opacity={0.65}>{getDateText(thread)}</Text>
    </Flex>
  )
}

const Thread = ({ thread, session, previousThread, nextThread, isPatientConvo }: { isPatientConvo: boolean, thread: IThread, session: IUser, previousThread?: IThread | undefined | null, nextThread?: IThread | undefined | null }) => {
  const [modalContent, setModalContent] = useState<any>(null);
  const parser = (input: string) => {
    return parse(input, {
      replace: domNode => {
        if ((domNode as ParserElement).name === 'img') {
          return <Image style={{ cursor: 'pointer' }} maxWidth={'200px'} src={(domNode as ParserElement).attribs.src} onClick={() => {
            setModalContent(<Image src={(domNode as ParserElement).attribs.src} />)
          }} />
        }
      }
    })
  }
  const isSelf = thread.createdBy.email.toLowerCase() === (session.email.toLowerCase() === 'g+5@palomahealth.com' ? 'ac.herbalmd@gmail.com' : session.email.toLowerCase())
  const isSelfNext = !!nextThread && nextThread.createdBy.email.toLowerCase() === (session.email.toLowerCase() === 'g+5@palomahealth.com' ? 'ac.herbalmd@gmail.com' : session.email.toLowerCase())
  const isSameDay = !previousThread ? false : moment(thread.createdAt).isSame(moment(previousThread.createdAt), 'day')
  const isShowTime = !nextThread || nextThread.createdBy.email.toLowerCase() !== thread.createdBy.email || nextThread.type === 'lineitem' || nextThread.type === 'note' || moment(nextThread.createdAt).diff(moment(thread.createdAt, 'minutes')) > -5
  let cls = ''
  if (!previousThread || (previousThread.createdBy.email.toLowerCase() !== thread.createdBy.email || previousThread.type === 'lineitem' || previousThread.type === 'note')) {
    cls += ' top'
  }
  if (!nextThread || (nextThread.createdBy.email.toLowerCase() !== thread.createdBy.email || nextThread.type === 'lineitem' || nextThread.type === 'note')) {
    cls += ' bottom'
  }

  if (thread.type === 'note') {
    const isShowTime2 = !nextThread || nextThread.type !== 'note' || moment(nextThread.createdAt).diff(moment(thread.createdAt, 'minutes')) > -5
    let isSamePreviousNote = false
    let isSameNextNote = false

    if (!!previousThread && previousThread.type === 'note') {
      if (isSelf && previousThread.createdBy.email.toLowerCase() !== thread.createdBy.email) {
        isSamePreviousNote = true
      } else if (!isSelf) {
        isSamePreviousNote = true
      }
    }
    if (!!nextThread && nextThread.type === 'note') {
      if (isSelf && nextThread.createdBy.email.toLowerCase() !== thread.createdBy.email) {
        isSameNextNote = true
      } else if (!isSelf) {
        isSameNextNote = true
      }
    }
    let cls2 = ''
    if (!isSamePreviousNote) {
      cls2 += ' top'
    }
    if (!isSameNextNote) {
      cls2 += ' bottom'
    }
    return (
      <Flex flexDirection={'column'} style={{ width: '100%', alignItems: isSelf ? 'flex-end' : 'flex-start' }} paddingX={'25px'}>
        {!isSameDay && <Box style={{ background: !isPatientConvo ? '#E7BFB9' : '#7F91D4', padding: '4px 8px', minWidth: '150px', borderRadius: '12px' }} mb={3} alignSelf={'center'}><Text fontSize={'14px'} fontWeight={600} textAlign={'center'}>{getDayText(thread)}</Text></Box>}
        <Flex flexDirection={'column'} alignItems={'inherit'} flex={1} mb={
          !nextThread ||
            (!!isSelf && !isSelfNext)
            || nextThread.type !== 'note' || isShowTime2 ? '16px' : '4px'}>
          {!isSelf ? (
            <SmsStyleBubbleNoteOther className={`${cls2}`}>
              <Flex alignItems={'center'} flex={1} mb={2}>
                <svg width="56" height="57" viewBox="0 0 56 57" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="0.170898" y="0.125" width="54.9319" height="55.879" rx="9.47101" fill="white" />
                  <path d="M31.066 40.8504V37.4961L39.5492 29.13L42.9506 32.4843L34.4673 40.8504H31.066ZM16.6604 32.9579V29.8009H27.8647V32.9579H16.6604ZM44.071 31.3794L40.6697 28.0251L41.8301 26.8806C42.1236 26.5912 42.4971 26.4466 42.9506 26.4466C43.4041 26.4466 43.7775 26.5912 44.071 26.8806L45.2314 28.0251C45.5249 28.3144 45.6716 28.6828 45.6716 29.13C45.6716 29.5772 45.5249 29.9456 45.2314 30.235L44.071 31.3794ZM16.6604 26.6439V23.4869H34.2672V26.6439H16.6604ZM16.6604 20.3299V17.1729H34.2672V20.3299H16.6604Z" fill="#344F7A" fill-opacity="0.65" />
                </svg>
                <Text ml={2} fontWeight={600}>Note added by {thread.createdBy.first} {thread.createdBy.last}</Text>
              </Flex>
              <section>{parser(thread.body || '')}</section>
            </SmsStyleBubbleNoteOther>
          ) : (
            <SmsStyleBubbleNoteMe className={`${cls2}`}>
              <Flex alignItems={'center'} flex={1} mb={2}>
                <svg width="56" height="57" viewBox="0 0 56 57" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="0.170898" y="0.125" width="54.9319" height="55.879" rx="9.47101" fill="white" />
                  <path d="M31.066 40.8504V37.4961L39.5492 29.13L42.9506 32.4843L34.4673 40.8504H31.066ZM16.6604 32.9579V29.8009H27.8647V32.9579H16.6604ZM44.071 31.3794L40.6697 28.0251L41.8301 26.8806C42.1236 26.5912 42.4971 26.4466 42.9506 26.4466C43.4041 26.4466 43.7775 26.5912 44.071 26.8806L45.2314 28.0251C45.5249 28.3144 45.6716 28.6828 45.6716 29.13C45.6716 29.5772 45.5249 29.9456 45.2314 30.235L44.071 31.3794ZM16.6604 26.6439V23.4869H34.2672V26.6439H16.6604ZM16.6604 20.3299V17.1729H34.2672V20.3299H16.6604Z" fill="#344F7A" fill-opacity="0.65" />
                </svg>
                <Text fontWeight={600} ml={2}>Note added by You</Text>
              </Flex>
              <section>{parser(thread.body || '')}</section>
            </SmsStyleBubbleNoteMe>
          )}
          {isShowTime2 && (
            <Text mt={1} fontSize={'12px'} opacity={0.65}>{getDateText(thread)}</Text>
          )}
        </Flex>

      </Flex>
    )
  }


  return (
    <Flex flexDirection={'column'} style={{ width: '100%', alignItems: isSelf ? 'flex-end' : 'flex-start' }} paddingX={'25px'}>
      {!isSameDay && <Box style={{ background: isPatientConvo ? '#E7BFB9' : '#7F91D4', padding: '4px 8px', minWidth: '150px', borderRadius: '12px' }} mb={3} alignSelf={'center'}><Text fontSize={'14px'} fontWeight={600} textAlign={'center'}>{getDayText(thread)}</Text></Box>}
      {!isSelf ? (
        <>
          {(!previousThread || previousThread.createdBy.email.toLowerCase() !== thread.createdBy.email || previousThread.type === 'lineitem' || previousThread.type === 'note') && (
            <>
              {!!thread.createdBy.photoUrl && (
                <Flex mb={2} alignItems={'center'}>
                  <MessageAvatarStyled photoUrl={thread.createdBy.photoUrl} />
                  <Text fontWeight={600}>{thread.createdBy.first} {thread.createdBy.last}</Text>
                  {/* <Text fontWeight={300} ml={2} fontSize={14}>{getDateText(thread)}</Text> */}
                </Flex>
              )}
              {!thread.createdBy.photoUrl && (
                <Flex mb={2} alignItems={'center'}>
                  <MessageAvatarNoPicStyled>
                    <Text color='white'>{thread.createdBy.first[0].toUpperCase()}{thread.createdBy.last[0].toUpperCase()}</Text>
                  </MessageAvatarNoPicStyled>
                </Flex>
              )}
            </>
          )
          }
        </>
      ) : (
        <>
          {(!previousThread || previousThread.createdBy.email.toLowerCase() !== thread.createdBy.email || previousThread.type === 'lineitem' || previousThread.type === 'note') && (
            <>
              <Flex mb={2} alignItems={'baseline'}>
                <Text fontWeight={600}>You</Text>
                {/* <Text fontWeight={300} ml={2} fontSize={14}>{getDateText(thread)}</Text> */}
              </Flex>
            </>)}
        </>
      )

      }

      {isSelf ? (
        <Flex flexDirection={'column'} alignItems={'inherit'} flex={1} mb={!nextThread || (nextThread.createdBy.email.toLowerCase() !== thread.createdBy.email || nextThread.type === 'lineitem' || nextThread.type === 'note') || isShowTime ? '16px' : '4px'}>
          <SmsStyleBubbleMe className={`${cls}`}>
            <section>{parser(thread.body || '')}</section>
          </SmsStyleBubbleMe>
          {isShowTime && (
            <Text mt={1} fontSize={'12px'} opacity={0.65}>{getDateText(thread)}</Text>
          )}
        </Flex>
      ) : (
        <>
          {(!thread.action || !thread.action.type || thread.action.type.indexOf('workflow') < 0) && (
            <Flex flexDirection={'column'} flex={1} mb={!nextThread || (nextThread.createdBy.email.toLowerCase() !== thread.createdBy.email || nextThread.type === 'lineitem') || isShowTime ? '16px' : '4px'}>
              <SmsStyleBubbleOther className={`${cls}`}>
                <section>{parser(thread.body || '')}</section>
              </SmsStyleBubbleOther>
              {isShowTime && (
                <Text mt={1} fontSize={'12px'} opacity={0.65}>{getDateText(thread)}</Text>
              )}
            </Flex>
          )}
        </>
      )}
      {/*       
      <ThreadLine type={thread.type} isSelf={isSelf} mr={3} />
      {!!thread.createdBy.photoUrl && <MessageAvatarStyled photoUrl={thread.createdBy.photoUrl} />}
      {!thread.createdBy.photoUrl && (
        <MessageAvatarNoPicStyled>
          <Text color='white'>{thread.createdBy.first[0].toUpperCase()}{thread.createdBy.last[0].toUpperCase()}</Text>
        </MessageAvatarNoPicStyled>
      )}
      <Flex flexDirection={'column'} flex={1} ml={2}>
        <Flex style={{ minHeight: '40px!important' }} paddingBottom={'6px'}>
          <Flex flex={1} flexDirection={['column', 'row', 'row']}>
            <Text fontWeight={500} fontSize='18px'>{!thread.action || !thread.action.type || thread.action.type.indexOf('workflow') < 0 ? Constants.IS_SCRAMBLING_MODE ? `${getMockedFirstName()} ${getMockedLastName()}` : `${thread.createdBy.first} ${thread.createdBy.last}` : `Worflows`}</Text>
            <Text ml={[0, 1, 1]} fontWeight={500} fontSize='18px' color={thread.type === 'note' ? '#fab347' : '#8494a5'}>{thread.type === 'customer' || thread.type === 'message' ? ' replied' : thread.type === 'note' ? ' added a note' : ''} </Text>
          </Flex>
          <Flex flexDirection={'column'} ml={3} alignItems='flex-end'>
            <Text color='#a5b2bd' fontSize={'12px'} >{getDateText(thread)}</Text>
            <Text color='#a5b2bd' fontSize={'12px'} >{thread.status}</Text>
          </Flex>
        </Flex>
        <MessageTextStyled fontSize={'14px'}>
          <section>{parser(thread.body || '')}</section>
        </MessageTextStyled>
        {thread._embedded?.attachments && thread._embedded.attachments.length > 0 && (
          <Flex flexWrap={'wrap'} width='100%' maxWidth={'100%'} alignItems='center'>
            <svg width="16" height="16" viewBox="0 0 24 24" style={{ marginRight: '12px' }} fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18.2031 1.9043C17.8391 1.91778 17.4791 1.98561 17.1351 2.10553C15.7514 2.57168 14.5643 3.63784 13.3856 4.81753L5.77017 12.4329C5.09356 13.1095 4.57202 13.9348 4.41417 14.9132C4.33339 15.4464 4.3802 15.9911 4.55078 16.5026C4.72135 17.0142 5.01081 17.478 5.3954 17.856C5.78209 18.2442 6.25668 18.5333 6.77893 18.699C7.30118 18.8647 7.85568 18.902 8.3954 18.8077C9.37202 18.6323 10.1899 18.1089 10.8185 17.4803L19.1834 9.11537C19.2844 9.03413 19.3671 8.93253 19.4262 8.81719C19.4853 8.70185 19.5195 8.57536 19.5265 8.44595C19.5334 8.31654 19.5131 8.1871 19.4668 8.06608C19.4204 7.94505 19.3491 7.83513 19.2574 7.74349C19.1658 7.65185 19.0559 7.58053 18.9349 7.53418C18.8138 7.48783 18.6844 7.46749 18.555 7.47447C18.4256 7.48145 18.2991 7.51561 18.1837 7.57472C18.0684 7.63382 17.9668 7.71656 17.8856 7.81753L9.51971 16.1825C9.12279 16.5794 8.57448 16.9015 8.07787 16.9901C7.58125 17.0788 7.15848 17.0225 6.69325 16.5581C6.20863 16.0735 6.15694 15.6665 6.23171 15.2012C6.30648 14.7369 6.59817 14.2006 7.06802 13.7308L14.6834 6.11537C15.8003 4.99937 16.8683 4.14922 17.712 3.86491C18.5566 3.58153 19.1622 3.64245 20.1351 4.61537C20.8625 5.34184 21.636 5.99722 21.8373 6.66368C21.9388 6.99599 21.9388 7.3283 21.7219 7.87476C21.5059 8.42122 21.0342 9.16891 20.136 10.0671C16.3071 13.8978 13.368 16.8351 11.7702 18.4329C10.3514 19.8517 9.0111 20.7988 7.81848 21.0572C6.62494 21.3166 5.49233 21.0406 4.01079 19.5572C2.5274 18.0738 2.00863 16.6375 2.16463 15.1449C2.31971 13.6514 3.22894 12.0314 4.90525 10.356C6.93233 8.32891 11.1637 4.44276 11.1637 4.44276C11.2664 4.36537 11.3517 4.2674 11.4143 4.15512C11.4769 4.04284 11.5153 3.91872 11.5272 3.79072C11.539 3.66272 11.524 3.53366 11.4831 3.4118C11.4421 3.28994 11.3762 3.17798 11.2895 3.08307C11.2028 2.98817 11.0972 2.91242 10.9796 2.86068C10.8619 2.80894 10.7347 2.78235 10.6062 2.78261C10.4776 2.78287 10.3505 2.80997 10.2331 2.86219C10.1156 2.91441 10.0103 2.99058 9.92402 3.08584C9.92402 3.08584 5.68525 6.92307 3.57787 9.02953C1.71048 10.896 0.534481 12.9028 0.318481 14.9723C0.102481 17.04 0.940635 19.0846 2.71294 20.8569C4.48525 22.6292 6.39787 23.2661 8.19325 22.8757C9.98863 22.4861 11.544 21.2557 13.068 19.7317C14.6659 18.1348 17.6049 15.1966 21.4339 11.3658C22.4493 10.3505 23.1065 9.44307 23.4526 8.56891C23.7988 7.69291 23.8126 6.82984 23.5976 6.1163C23.1665 4.69014 22.0099 3.89353 21.4339 3.31845C20.7729 2.65753 20.0419 2.2403 19.2988 2.04922C18.942 1.95054 18.5732 1.90207 18.2031 1.90522V1.9043Z" fill="#a5b2bd" />
            </svg>
            {thread._embedded.attachments.map(x => {
              return (<AttachmentFileFromHS file={x} />)
            })}
          </Flex>
        )}
      </Flex>
      <Modal
        isOpen={!!modalContent}
        onBackgroundClick={() => setModalContent(null)}
        onEscapeKeydown={() => setModalContent(null)}>
        <Flex flexDirection={'column'}

          style={{
            margin: '0 auto',
            alignSelf: 'center',
            borderRadius: '20px',
            position: 'relative',
            backgroundColor: 'white',
            padding: '24px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            maxHeight: 'calc(100vh * 0.8)',
          }}>{modalContent}
          <Box onClick={() => setModalContent(null)} style={{ cursor: 'pointer', position: 'absolute', top: '-10px', right: '-10px', height: '34px', width: '34px', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#F8A294', padding: '6px', borderRadius: '50%', color: 'white' }}>
            X
          </Box>
        </Flex>
      </Modal> */}
    </Flex>
  )
}

const Conversations = ({ conversation, session, isPatientConvo }: { conversation: Array<IThreadOutput> | null, session: IUser, isPatientConvo: boolean }) => {
  return (
    <>
      {conversation?.map(x => {
        return (<React.Fragment key={x.id}>
          {JSON.parse(JSON.stringify(x._embedded.threads))
            .reverse()
            .map((uu: IThread) => uu)
            .filter((x: IThread) => (x.state === 'published' || x.type === 'lineitem') && x.body !== 'initiate_1' && (x.type !== 'note' || (!x.body) || !x.body.startsWith('Set conversation status to')))
            .map((thread: IThread, i: number, arr: IThread[]) => {
              if (thread.type !== 'lineitem')
                return (<Thread isPatientConvo={isPatientConvo} session={session} thread={thread} key={thread.id} previousThread={i > 0 ? arr[i - 1] : null} nextThread={i < arr.length - 2 ? arr[i + 1] : null} />)
              if (thread.type === 'lineitem') {
                return (<LineItem isPatientConvo={isPatientConvo} thread={thread} key={thread.id} previousThread={i > 0 ? arr[i - 1] : null} />)
              }
            })}
        </React.Fragment>)
      })
      }
    </>
  )
}



const CommunicatorMessagerEmbedded: React.FC<ICommunicatorMessagerProps> = ({ conversationId, session, onSelectConversation, athenaId, careTeamConvoId, isHpiEmbed }) => {
  const [conversation, setConversation] = useState<Array<IThreadOutput> | null>(null);
  const messagesEndRef = React.createRef<any>()
  const [currentMailboxConvoId, setCurrentMailboxConvoId] = useState<string | null | undefined>(conversationId)
  const [convoId, setConvoId] = useState<string | null>(null);
  const [isReplying, setIsReplying] = useState<boolean>(false);
  const [isNoting, setIsNoting] = useState<boolean>(false);
  const [isTagging, setIsTagging] = useState(false)
  const [isInputExpanded, setIsInputExpanded] = useState(false)
  const [selectedMailbox, setSelectedMailbox] = useState<{
    label: 'Clinical Team',
    value: 'CLINICAL'
  } | {
    label: 'Physician Mailbox',
    value: 'MEMBER',
    disabled?: true | false
  } | {
    label: 'Care Team Mailbox',
    value: 'CARETEAM',
    disabled?: true | false
  }>({
    label: 'Physician Mailbox',
    value: 'MEMBER',
    disabled: !conversationId
  })
  const toastId = useRef<any>(null);
  let navigate = useNavigate();
  const { data: dataClinicalTeamConversationId, loading: loadingClinicalTeamConversationId, error: errorClinicalTeamConversationId, refetch: refetchClinicalTeamConversationId } = useQuery<IGetClinicalTeamConversationIdOutput, IGetClinicalTeamConversationIdInput>(GET_CLINICAL_TEAM_CONVERSATION_ID, {
    variables: {
      athenaId
    }
  })
  // console.log({
  //   dataClinicalTeamConversationId,
  //   loadingClinicalTeamConversationId,
  //   errorClinicalTeamConversationId
  // })
  const [saveReply, { data: dataSaveReply, loading: loadingSaveReply }] = useMutation<IReplyToConversationOutput, IReplyToConversationInput>(REPLY_TO_CONVERSATION, {
    refetchQueries: [{
      query: GET_CONVERSATIONS, variables: {
        status: 'closed'
      }
    }, {
      query: GET_CONVERSATIONS, variables: {
        status: 'open'
      }
    }, {
      query: GET_CONVERSATION_BY_ID,
      variables: {
        conversationId: currentMailboxConvoId
      }
    }],
    awaitRefetchQueries: true,
    onError: (e) => {
      if (e && toastId && toastId.current) {
        toast.update(toastId.current, {
          render: (<Flex flexDirection={'column'}>
            <Text fontWeight={400}>Failed to send the message</Text>
            <Text fontWeight={300}>On conversation {currentMailboxConvoId}</Text>
          </Flex>),
          type: toast.TYPE.ERROR,
          isLoading: false,
          autoClose: 5000,
          closeButton: null // The closeButton defined on ToastContainer will be used
        });
      }
    },
    onCompleted: (e) => {
      if (!!e && !!e.providerReplyToConversation && !!e.providerReplyToConversation.redirectTo) {
        navigate(`/messages/${e.providerReplyToConversation.redirectTo}`)
      }
    }
  })
  const [addNote, { data: dataAddNote, loading: loadingAddNote }] = useMutation<IAddNoteToConversationOutput, IAddNoteToConversationInput>(ADD_NOTE_TO_CONVERSATION, {
    refetchQueries: [{
      query: GET_CONVERSATIONS, variables: {
        status: 'closed'
      }
    }, {
      query: GET_CONVERSATIONS, variables: {
        status: 'open'
      }
    }, {
      query: GET_CONVERSATION_BY_ID,
      variables: {
        conversationId: currentMailboxConvoId
      }
    }],
    awaitRefetchQueries: true,
    onError: (e) => {
      if (e && toastId && toastId.current) {
        toast.update(toastId.current, {
          render: (<Flex flexDirection={'column'}>
            <Text fontWeight={400}>Failed to send the note</Text>
            <Text fontWeight={300}>On conversation {currentMailboxConvoId}</Text>
          </Flex>),
          type: toast.TYPE.ERROR,
          isLoading: false,
          autoClose: 5000,
          closeButton: null // The closeButton defined on ToastContainer will be used
        });
      }
    }
  })

  const [reassignConversation, { data: ReassignConversationData, loading: loadingReassignConversation, error: errorReassignConversation }] = useMutation<IProviderReassignConversationOutput, IProviderReassignConversationInput>(REASSIGN_CONVERSATION, {
    refetchQueries: [{
      query: GET_CONVERSATIONS, variables: {
        status: 'closed'
      }
    }, {
      query: GET_CONVERSATIONS, variables: {
        status: 'open'
      }
    }, {
      query: GET_CONVERSATION_BY_ID,
      variables: {
        conversationId: currentMailboxConvoId
      }
    }],
    onError: (e) => {
      if (e && toastId.current) {
        toast.update(toastId.current, {
          render: 'Failed to reassign the conversation',
          type: toast.TYPE.ERROR,
          isLoading: false,
          autoClose: 5000,
          closeButton: null // The closeButton defined on ToastContainer will be used
        });
      }
    }
  })
  const [changeStatusConversation, { data: ChangeStatusConversationData, loading: loadingChangeStatusConversation, error: errorChangeStatusConversation }] = useMutation<IProviderChangeStatusConversationOutput, IProviderChangeStatusConversationInput>(CHANGE_CONVERSATION_STATUS, {
    refetchQueries: [{
      query: GET_CONVERSATIONS, variables: {
        status: 'closed'
      }
    }, {
      query: GET_CONVERSATIONS, variables: {
        status: 'open'
      }
    }, {
      query: GET_CONVERSATION_BY_ID,
      variables: {
        conversationId: currentMailboxConvoId
      }
    }],
    onError: (e) => {
      if (e && toastId.current) {
        toast.update(toastId.current, {
          render: 'Failed to change the conversation status',
          type: toast.TYPE.ERROR,
          isLoading: false,
          autoClose: 5000,
          closeButton: null // The closeButton defined on ToastContainer will be used
        });
      }
    }
  })
  // const { data, loading, error, refetch } = useQuery<IGetConversationByIdOutput, IIGetConversationByIdInput>(GET_CONVERSATION_BY_ID, {
  //   variables: { conversationId: conversationId.toString() }
  // })
  const [loadConversationById, { data, loading, error, refetch }] = useLazyQuery<IGetConversationByIdOutput, IIGetConversationByIdInput>(GET_CONVERSATION_BY_ID)

  useEffect(() => {
    if (!conversationId) {
      return
    }
    setConvoId(conversationId)
    loadConversationById({
      variables: {
        conversationId: conversationId.toString()
      }
    })
  }, [])

  useEffect(() => {
    if (loading) return
    if (!data || !data.providerGetConversationById || !data.providerGetConversationById.page) return
    setConversation([data.providerGetConversationById])
    setTimeout(() => {

    })
  }, [data, loading])

  useEffect(() => {
    if (selectedMailbox.value === 'CLINICAL') {
      return
    }if (selectedMailbox.value === 'CARETEAM') {
      if(!careTeamConvoId) return
      setCurrentMailboxConvoId(careTeamConvoId ? careTeamConvoId.toString() : undefined)
      if (!careTeamConvoId) {
        return
      }
      setConvoId(careTeamConvoId)
      loadConversationById({
        variables: {
          conversationId: careTeamConvoId.toString()
        }
      })
    } else {
      setCurrentMailboxConvoId(conversationId ? conversationId.toString() : undefined)
      if (!conversationId) {
        return
      }
      setConvoId(conversationId)
      loadConversationById({
        variables: {
          conversationId: conversationId.toString()
        }
      })
    }
  }, [selectedMailbox])

  useEffect(() => {
    if (selectedMailbox.value === 'CLINICAL') {
      if (!loadingClinicalTeamConversationId && dataClinicalTeamConversationId && dataClinicalTeamConversationId.getPatientClinicalTeamActiveConversationId) {
        setCurrentMailboxConvoId(dataClinicalTeamConversationId.getPatientClinicalTeamActiveConversationId.toString())
        setConvoId(dataClinicalTeamConversationId.getPatientClinicalTeamActiveConversationId.toString())
        loadConversationById({
          variables: {
            conversationId: dataClinicalTeamConversationId.getPatientClinicalTeamActiveConversationId.toString()
          }
        })

      }
    } else {
      return
    }
  }, [selectedMailbox, loadingClinicalTeamConversationId, dataClinicalTeamConversationId])

  useEffect(() => {
    setTimeout(() => {
      if (!!messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
      } else {
      }
    }, 100)
  }, [conversation])

  useEffect(() => {
    if (!!conversationId && conversationId !== convoId) {
      setConvoId(conversationId)
      if (convoId !== null)
        setConversation(null)
    }
  }, [conversationId])
  const handleKeyPress = useCallback((event: any) => {
    if (isNoting || isReplying) return

    if (!!event.key && event.key === 'n') {
      setIsNoting(true)
    }
  }, []);

  useEffect(() => {
    return () => {
      if (toastId && toastId.current) {
        toast.dismiss(toastId.current)
      }
    }
  }, [])

  const toggleReplying = () => {
    if (!!isNoting) return
    setIsReplying(!isReplying)
  }
  const openReplying = () => {
    if (!!isNoting) return
    setIsReplying(true)
  }
  const toggleNoting = () => {
    if (!!isReplying) return
    setIsNoting(!isNoting)
  }
  const openNoting = () => {
    if (!!isReplying) return
    setIsNoting(!isNoting)
  }
  const onDeleteDraft = () => {
    setIsNoting(false)
    setIsReplying(false)
  }
  const onSendReplyHandler = async (html: string, assignedTo: number, status: string, attachments: Array<{
    fileName: string,
    mimeType: string,
    data: string,
    size: number,
  }> | null) => {
    console.log('onSendReplyHandler', { conversationId, currentMailboxConvoId })
    if (!currentMailboxConvoId) return

    const primaryCustomer = data?.providerGetConversationById.primaryCustomer.id
    if (!primaryCustomer) return
    toastId.current = toast(<Flex flexDirection={'column'}>
      <Text fontWeight={400}>Sending {!!isNoting ? 'note' : 'message'}</Text>
      <Text fontWeight={300}>On conversation {currentMailboxConvoId}</Text>
    </Flex>, { autoClose: false, isLoading: true, closeButton: false });

    let sanitizedAttachments = []
    for (let index = 0; index < (attachments || []).length; index++) {
      const element = (attachments || [])[index];
      sanitizedAttachments.push({
        fileName: element.fileName,
        data: element.data,
        mimeType: element.mimeType
      })
    }
    if (!!isNoting) {
      const dta = {
        text: html,
        assignedTo,
        conversationId: currentMailboxConvoId,
        status,
        userId: session.helpscoutId,
        attachments: sanitizedAttachments
      }

      await addNote({
        variables: {
          ...dta
        }
      })

      toggleNoting()
      toast.update(toastId.current, {
        render: 'Note Sent!',
        type: toast.TYPE.SUCCESS,
        isLoading: false,
        autoClose: 5000,
        closeButton: null // The closeButton defined on ToastContainer will be used
      });
    } else if (isReplying) {
      const dta = {
        text: html,
        assignedTo,
        conversationId: currentMailboxConvoId,
        customerId: primaryCustomer,
        status,
        userId: session.helpscoutId,
        attachments: sanitizedAttachments
      }

      await saveReply({
        variables: {
          ...dta
        }
      })
      toast.update(toastId.current, {
        render: 'Reply Sent!',
        type: toast.TYPE.SUCCESS,
        isLoading: false,
        autoClose: 5000,
        closeButton: null // The closeButton defined on ToastContainer will be used
      });
      // refetch()
      toggleReplying()
    }
  }
  const bg = selectedMailbox && selectedMailbox.value === 'CLINICAL' ? '#F9E1DD' : '#C0CDEC'
  if (!!loading) {
    return (
      <Box style={{ height: '100%', background: bg, flex: 1, borderRadius: '20px', paddingTop: '20px', maxWidth: isHpiEmbed?'initial':'460px', minWidth: '460px', width: '460px' }}>

        <Flex flex={1} marginTop={4} width='100%' height='100%' flexDirection={'column'} justifyContent='center' alignItems={'center'} >
          {/* <PleaseWait
        text={` `} /> */}
          <LoadingMessages style={{ maxWidth: '50%' }} />
        </Flex>
      </Box>)
  }

  const isPatientConvo = true // TODO check mailboxid : data?.providerGetConversationById.mailboxId === new clinicalTeam Mailbox Id

  if (!!loadingClinicalTeamConversationId) {
    return <Box style={{ height: '100%', background: bg, flex: 1, borderRadius: '20px', paddingTop: '20px', maxWidth: isHpiEmbed?'initial':'460px', minWidth: '460px', width: '460px' }}>

      <Flex style={{ flex: 1, height: '100%', maxHeight: '100%', overflow: 'hidden' }}>
        <PleaseWait />
      </Flex>
    </Box>
  }

  return (
    <Box style={{ height: '100%', background: bg, flex: 1, borderRadius: '20px', paddingTop: '20px', maxWidth: isHpiEmbed?'initial':'460px', minWidth: '460px', width: '460px' }}>

      <Flex flex={1} flexDirection={'column'} style={{ height: '100%', maxHeight: '100%', overflowY: 'auto' }}>

        <Flex ml={'16px'} mr={4} alignItems='center' flexDirection={['column-reverse', 'row', 'row']}>
          <Select
            options={[{
              label: 'Clinical Team',
              value: 'CLINICAL'
            }, {
              label: 'Physician Mailbox',
              value: 'MEMBER',
              disabled: true
            }, {
              label: 'Care Team Mailbox',
              value: 'CARETEAM',
              disabled: true
            }]}
            isSearchable={false}
            styles={ReactSelectStyles.mailboxesStyles}
            value={selectedMailbox}
            onChange={(option: any, actionMeta: ActionMeta<any>) => {
              if (!option) return
              setSelectedMailbox(option)
            }}
          />
          <Flex flex={1}></Flex>

          {!loading && !!data && !!data?.providerGetConversationById && !!data?.providerGetConversationById && (
            <ConversationStatusCard status={data?.providerGetConversationById.status} />
          )}
        </Flex>

        {selectedMailbox.value === 'CLINICAL'
          && !loadingClinicalTeamConversationId
          && !dataClinicalTeamConversationId?.getPatientClinicalTeamActiveConversationId && (
            <NoClinicalTeamConversation refetchClinicalTeamConversationId={refetchClinicalTeamConversationId} athenaId={athenaId} />
          )}

        {selectedMailbox.value !== 'CLINICAL' && !conversationId && (
          <Flex flexDirection={'column'} justifyContent={'center'} alignItems={'center'} flex={1}>
            <Text maxWidth={'63%'} textAlign={'center'} mb={4}>No Physician Conversation found</Text>

          </Flex>
        )}

        {(selectedMailbox.value !== 'CLINICAL' ||
          (!loadingClinicalTeamConversationId && dataClinicalTeamConversationId
            &&
            !!dataClinicalTeamConversationId?.getPatientClinicalTeamActiveConversationId)) && (
            <MessengerEmbeddedInner
              loading={loading}
              data={data}
              conversationId={currentMailboxConvoId!}
              isNoting={isNoting}
              isReplying={isReplying}
              isPatientConvo={selectedMailbox.value !== 'CLINICAL'}
              conversation={conversation}
              session={session}
              setIsTagging={setIsTagging}
              setIsNoting={setIsNoting}
              setIsReplying={setIsReplying}
              reassignConversation={reassignConversation}
              changeStatusConversation={changeStatusConversation}
              openNoting={openNoting}
              openReplying={openReplying}
              isTagging={isTagging}
              messagesEndRef={messagesEndRef}
              onSendReplyHandler={onSendReplyHandler}
              isHpiEmbed={isHpiEmbed}
            />
          )}
      </Flex>
    </Box>);
}

export default withSession(CommunicatorMessagerEmbedded)